import { GridReadyEvent, RowSelectedEvent } from "@ag-grid-community/core";
import { Component, TemplateRef, ViewChild, ViewEncapsulation } from "@angular/core";

import { ActivatedRoute } from "@angular/router";

import { AgFns, GridOptionsExt } from "app/shared/ag-fns";
import { DialogBaseComponent } from "app/shared/dialog-base.component";
import { takeUntil } from "rxjs/operators";
import { Manufacturer, ManufacturerImage, ProductType } from "../model/entities/entity-model";
import { DomainBaseComponent, wrapBusy } from "../shared/domain-base.component";
import { DomainService } from "../shared/domain.service";
import { EditManufacturerDialogComponent } from "./edit-manufacturer-dialog.component";
import { UniAgFns } from "@/shared/uni-ag-fns";

@Component({
  selector: "app-manufacturer",
  templateUrl: "./manufacturer.component.html",

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ManufacturerComponent extends DomainBaseComponent {
  @ViewChild("manufButtonsCell") manufButtonsCell: TemplateRef<any>;

  manufGridOptions: GridOptionsExt;

  constructor(protected domainService: DomainService, protected route: ActivatedRoute) {
    super(domainService);

    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  async updateFromContext() {
    this.manufGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onManufGridReady,
      onRowSelected: this.onRowSelected,
      onFilterChanged: this.onFilterChanged,
      rowModelType: "serverSide",
      onPaginationChanged: (params) => {
        if (params.newPage) {
          this.replaceLocation();
        }
      },
    });
    this.captureLocation(this.manufGridOptions, "id");

    this.isPageReady = true;
  }

  async canDeactivate() {
    var ok = await super.canDeactivate();
    if (!ok) return false;
    this.uow.clearEntities(Manufacturer);
    this.uow.clearEntities(ManufacturerImage);
    return true;
  }

  // This method is called AFTER ngAfterViewInit - this is important
  // because the ColDef's here reference TemplateRef's above that will not be resolved until here.
  async onManufGridReady(event: GridReadyEvent) {
    const colDefs = [
      { ...AgFns.createButtonProps("Edit", this.editManufacturer.bind(this), { label: "Edit" }), ...AgFns.fixWidth(100) },
      { headerName: "Name", field: "name", filter: "agTextColumnFilter", ...AgFns.flexWidth(120) },
      { ...UniAgFns.getActiveStatusColDef("activeStatus.name"), ...AgFns.fixWidth(120) },
      {
        // ManufacturerFeedStatus is a cached lookup, same as ActiveStatus above, so the
        // navigation resolves without expanding it on the server-side query.
        headerName: "Manufacturer Feed",
        field: "manufacturerFeedStatus.name",
        ...AgFns.fixWidth(160),
      },
      {
        headerName: "P/O Doc Submit Type",
        field: "poDocSubmitType.name",
        filter: "agTextColumnFilter",
        ...AgFns.fixWidth(180),
      },
      {
        headerName: "Lead Time/Days",
        field: "leadTimeDays",
        filter: "agNumberColumnFilter",
        ...AgFns.fixWidth(140),
      },
      {
        headerName: "Transit Time/Days",
        field: "transitTimeDays",
        filter: "agNumberColumnFilter",
        ...AgFns.fixWidth(160),
      },
      {
        headerName: "Account Reference",
        field: "accountReferenceName",
        filter: "agTextColumnFilter",
        ...AgFns.fixWidth(180),
      },
      {
        headerName: "Last Backorder Request Date",
        field: "startDate",
        filter: "agDateColumnFilter",
        ...AgFns.fixWidth(220),
      },
      UniAgFns.getEntityDeleteColDef(this.onDelete.bind(this), {
        canDisplay: () => true,
      }),
    ];
    const sortModel = [{ colId: "name", sort: "asc" as const }];

    AgFns.initGrid(event, colDefs, sortModel);

    this.updateDatasource();

    await this.applyLocation();
  }

  updateDatasource() {
    const gridApi = this.manufGridOptions.apiX;
    if (gridApi == null) {
      return;
    }

    const ds = AgFns.buildDatasource(() => this.dbQueryService.createManufacturerQuery());

    gridApi.setGridOption("serverSideDatasource", ds);
  }

  async onRowSelected(e: RowSelectedEvent) {
    // check if a deselect event and ignore
    if (!e.node.isSelected()) {
      return;
    }
    let manuf = e.data;
    if (!manuf) {
      return;
    }
    this.replaceLocation(manuf.id);
  }

  @wrapBusy
  async onDelete(mft: Manufacturer) {
    const inUse = await this.dbQueryService.checkIfInUse(mft, ProductType, "manufacturerId");
    if (inUse) {
      this.dialogService.showOkMessage("Cannot Delete", "Cannot delete Manufacturer with associated Product Types.");
      return false;
    }

    const ok = await this.dialogService.yesNoDialog("Delete this Manufacturer?", "Are you sure?");
    if (!ok) return;

    if (mft.manufacturerCryptonym) {
      mft.manufacturerCryptonym.entityAspect.setDeleted();
    }

    mft.entityAspect.setDeleted();
    await this.dbSaveService.saveChanges();
    this.manufGridOptions.apiX.refreshServerSide();
    this.toastr.success("Manufacturer successfully deleted");
  }

  onFilterChanged() {
    this.replaceLocation();
  }

  // updateLocation(key: any = null) {
  //   const urlTree = this.router.createUrlTree([
  //     "/references/manage/manufacturers",
  //   ]);
  //   const url = AgFns.buildGridRouteParamsUrl(
  //     urlTree,
  //     this.manufGridOptions,
  //     key && key.toString()
  //   );
  //   this.domainService.location.replaceState(url);
  // }

  async editManufacturer(manufacturer: Manufacturer) {
    const r = DialogBaseComponent.open(EditManufacturerDialogComponent, this, {
      manufacturer: manufacturer,
    });

    if (r) {
      this.manufGridOptions.apiX.refreshCells();
    }
  }

  async addManufacturer() {
    const r = await DialogBaseComponent.open(EditManufacturerDialogComponent, this, {});
    if (r) {
      this.manufGridOptions.apiX.refreshServerSide();
    }
  }
}
